/**
 * parts/goose.js — the player's goose, and the first-person wing hands.
 *
 * The goose is the thing players look at for the whole session, so it gets the
 * detail budget: a plump teardrop body, a swept S-curve neck built as a real
 * tube, a two-piece bill, glinting eyes, folded wings with feather notches and
 * broad orange webbed feet.
 *
 * ANIMATION CONTRACT — `avatar/goose-controller.js` writes these every frame
 * and expects these rest values:
 *   parts.neck.rotation.x  rests at −0.22   (waddle bob)
 *   parts.neck.rotation.z  rests at 0       (idle glance)
 *   parts.body.scale.y     rests at 0.82    (breathing)
 *   parts.head.position.x  rests at 0       (idle glance)
 * The neck and head are GROUPS with no scale of their own, so cosmetics parented
 * to the head anchors ride along without inheriting the breathing squash.
 *
 * @module assets3d/parts/goose
 */

import * as THREE from 'three';
import { ART, css, shade, tint } from '../palette.js';
import { paint } from '../textures.js';
import { geo, mat, mesh, makeAnchor, tube, teardrop, webbedFoot } from './core.js';

/** Rest value of `parts.body.scale.y`. The controller hard-codes this. */
const BREATH_REST_Y = 0.82;
/** Rest value of `parts.neck.rotation.x`. The controller hard-codes this. */
const NECK_REST_X = -0.22;

/**
 * A feather face: base colour washing out to a pale tip, with a faint central
 * shaft. Sold as one small texture rather than extra geometry.
 * @param {number} base
 * @param {number} tipColor
 * @returns {THREE.CanvasTexture}
 */
function featherFace(base, tipColor) {
  return paint(`feather-${base}-${tipColor}`, 64, 128, (ctx, w, h) => {
    const g = ctx.createLinearGradient(0, h, 0, 0);
    g.addColorStop(0, css(base));
    g.addColorStop(0.55, css(tint(base, 0.18)));
    g.addColorStop(1, css(tipColor));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // Shaft.
    ctx.strokeStyle = `rgba(0,0,0,0.10)`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(w / 2, h);
    ctx.lineTo(w / 2, h * 0.06);
    ctx.stroke();
    // Barb hatching.
    ctx.lineWidth = 1;
    for (let i = 0; i < 22; i++) {
      const y = h - (i / 22) * h * 0.92;
      ctx.strokeStyle = `rgba(0,0,0,${0.05 - i * 0.0016})`;
      ctx.beginPath();
      ctx.moveTo(w / 2, y);
      ctx.lineTo(w * 0.06, y + h * 0.05);
      ctx.moveTo(w / 2, y);
      ctx.lineTo(w * 0.94, y + h * 0.05);
      ctx.stroke();
    }
  });
}

/**
 * A single feather, lying in the XZ plane with its tip toward −Z and its quill
 * at the origin. UVs run base-to-tip so `featherFace` maps correctly.
 *
 * @param {number} len
 * @param {number} width
 * @returns {THREE.BufferGeometry}
 */
function featherGeo(len, width) {
  return geo(`feather-${len}-${width}`, () => {
    const s = new THREE.Shape();
    const w = width / 2;
    s.moveTo(0, 0);
    s.quadraticCurveTo(w * 1.05, len * 0.26, w * 0.86, len * 0.74);
    s.quadraticCurveTo(w * 0.62, len * 1.0, 0, len);
    s.quadraticCurveTo(-w * 0.62, len * 1.0, -w * 0.86, len * 0.74);
    s.quadraticCurveTo(-w * 1.05, len * 0.26, 0, 0);
    const g = new THREE.ShapeGeometry(s, 5);

    // ShapeGeometry UVs are in shape units; normalise them to 0–1 so the
    // gradient lands base-to-tip regardless of feather size.
    const uv = g.attributes.uv;
    for (let i = 0; i < uv.count; i++) {
      uv.setXY(i, (uv.getX(i) + w) / (w * 2), uv.getY(i) / len);
    }
    g.rotateX(-Math.PI / 2);   // +Y (tip) becomes −Z
    return g;
  });
}

/**
 * The player's goose (GDD 3.2). Origin at the feet, facing −Z.
 *
 * @param {{ cosmetics?: object, color?: number }} [opts] `color` tints the
 *   plumage; cosmetic skins depend on it recolouring body, neck, head and wings
 *   together rather than the body alone.
 * @returns {THREE.Group}
 */
export function createGoose(opts = {}) {
  const g = new THREE.Group();
  g.name = 'goose';

  const bodyColor = opts.color ?? ART.cream;
  const plume = mat(bodyColor, { rough: 0.86 });
  const plumeShaded = mat(shade(bodyColor, 0.09), { rough: 0.88 });
  const billColor = 0xf0a03a;
  const bill = mat(billColor, { rough: 0.44 });
  const footMat = mat(shade(billColor, 0.06), { rough: 0.55 });

  // ── Body ──────────────────────────────────────────────────────────────────
  const body = mesh(teardrop(0.30, { seg: 18, taper: 0.40, lift: 0.32, chest: 1.06 }), plume);
  body.scale.set(1, BREATH_REST_Y, 1.30);
  body.position.set(0, 0.44, 0.04);
  body.name = 'goose-body';
  g.add(body);

  // ── Neck (group: the controller rotates this) ──────────────────────────────
  const neck = new THREE.Group();
  neck.name = 'goose-neck';
  neck.position.set(0, 0.60, -0.11);
  neck.rotation.x = NECK_REST_X;
  g.add(neck);

  // A genuine S: out from the shoulder, back through the middle, forward again
  // under the skull. This one curve is most of what makes the bird read as a
  // goose rather than a duck.
  const neckCurve = [
    new THREE.Vector3(0, 0, 0.02),
    new THREE.Vector3(0, 0.11, -0.048),
    new THREE.Vector3(0, 0.23, -0.030),
    new THREE.Vector3(0, 0.34, -0.014),
    new THREE.Vector3(0, 0.42, -0.036),
  ];
  const neckMesh = mesh(
    tube(neckCurve, 0.088, { key: 'goose-neck', segments: 18, radial: 9, taper: 0.60 }),
    plume,
  );
  neck.add(neckMesh);

  // ── Head (group: the controller shifts this sideways to glance) ────────────
  const head = new THREE.Group();
  head.name = 'goose-head';
  head.position.set(0, 0.44, -0.045);
  neck.add(head);

  const skull = mesh(
    geo('goose-skull', () => new THREE.SphereGeometry(0.108, 14, 11)),
    plume,
  );
  skull.scale.set(1.0, 1.06, 1.16);
  head.add(skull);

  // Bill: an upper mandible with a darker nail at the tip, and a slimmer lower
  // one, split by a shadow gap. Two pieces is the whole difference between
  // "goose" and "traffic cone stapled to a sphere".
  const beak = new THREE.Group();
  beak.name = 'goose-beak';
  beak.position.set(0, -0.012, -0.098);
  head.add(beak);

  const upper = mesh(
    geo('goose-bill-upper', () => new THREE.ConeGeometry(0.052, 0.165, 9)),
    bill,
  );
  upper.rotation.x = -Math.PI / 2;
  upper.scale.set(1.16, 1, 0.66);
  upper.position.set(0, 0.012, -0.072);
  beak.add(upper);

  const lower = mesh(
    geo('goose-bill-lower', () => new THREE.ConeGeometry(0.044, 0.138, 8)),
    mat(shade(billColor, 0.14), { rough: 0.5 }),
  );
  lower.rotation.x = -Math.PI / 2;
  lower.scale.set(1.05, 1, 0.44);
  lower.position.set(0, -0.026, -0.060);
  beak.add(lower);

  const nail = mesh(
    geo('goose-bill-nail', () => new THREE.SphereGeometry(0.021, 7, 6)),
    mat(shade(billColor, 0.34), { rough: 0.42 }),
  );
  nail.scale.set(1.15, 0.62, 0.9);
  nail.position.set(0, 0.010, -0.148);
  beak.add(nail);

  // ── Eyes ──────────────────────────────────────────────────────────────────
  for (const s of [-1, 1]) {
    const eye = mesh(
      geo('goose-eye', () => new THREE.SphereGeometry(0.0215, 9, 7)),
      mat(ART.ink, { rough: 0.22 }),
      { cast: false },
    );
    eye.position.set(0.070 * s, 0.030, -0.056);
    head.add(eye);

    // The glint is what makes the bird look alive, so it is emissive rather
    // than merely lit — and emissive rather than UNLIT, because cosmetics
    // recolour the goose by walking its materials and a MeshBasicMaterial has
    // no emissive channel to write to.
    const glint = mesh(
      geo('goose-glint', () => new THREE.SphereGeometry(0.0072, 6, 5)),
      mat(0xffffff, { rough: 0.4, emissive: 0xffffff, emissiveIntensity: 0.9 }),
      { cast: false, receive: false },
    );
    glint.position.set(0.079 * s, 0.041, -0.070);
    head.add(glint);
  }

  // ── Wings ─────────────────────────────────────────────────────────────────
  const wingTex = featherFace(shade(bodyColor, 0.10), tint(bodyColor, 0.55));
  const featherMat = mat(0xffffff, {
    rough: 0.9, map: wingTex, side: THREE.DoubleSide,
  });

  for (const s of [-1, 1]) {
    const wing = new THREE.Group();
    wing.name = `wing-${s < 0 ? 'left' : 'right'}`;
    // Set into the flank rather than bolted onto it — at 0.275 the wings read
    // as paddles stuck out sideways, especially from behind.
    wing.position.set(0.235 * s, 0.47, 0.03);
    // A touch of asymmetry: one wing sits marginally prouder than the other,
    // which reads as a real bird rather than a mirrored model.
    wing.rotation.z = (s < 0 ? 0.04 : -0.07);
    wing.rotation.y = (s < 0 ? 0.05 : -0.02);
    g.add(wing);

    const mass = mesh(
      geo('goose-wing-mass', () => new THREE.SphereGeometry(0.175, 11, 9)),
      plumeShaded,
    );
    mass.scale.set(0.40, 0.80, 1.10);
    wing.add(mass);

    // Folded primaries lying ALONG the flank and sweeping to the tail. Angled
    // out from the body they looked like a broken fan floating in mid-air.
    //
    // They are flat planes, so they must never sit edge-on to the viewer or
    // they render as a stray dark line off the bird's back. Pitching them down
    // onto the flank keeps a face turned outward from every angle a player
    // actually sees, and stops any part poking past the wing's silhouette.
    for (let i = 0; i < 3; i++) {
      const f = mesh(featherGeo(0.115 + i * 0.015, 0.062), featherMat, { receive: false });
      f.position.set(-0.030 * s, 0.010 - i * 0.024, 0.070);
      f.rotation.set(
        -0.18 - i * 0.05,                       // lie back along the body
        // Swept IN toward the spine. The body narrows sharply toward the tail,
        // so feathers carried straight back leave the silhouette and read as
        // pale blades hanging off the bird — obvious on the darker NPC geese.
        Math.PI - (0.10 + i * 0.05) * s,
        (0.34 + i * 0.06) * s,                  // roll the face outward
      );
      wing.add(f);
    }
  }

  // ── Tail ──────────────────────────────────────────────────────────────────
  // Pinned at the body's actual rear tip. The teardrop's tail end sits at
  // roughly (0, 0.51, 0.43) once scaled, and mounting the fan forward of that
  // buried the whole thing inside the body — from behind, which is the camera
  // angle for most of the game, the goose had no tail at all.
  const tail = new THREE.Group();
  tail.name = 'goose-tail';
  // NEGATIVE pitch is up: rotating +X by θ sends the fan's +Z axis to
  // (0, −sin θ, cos θ), so a positive angle drops the tail over the feet.
  tail.position.set(0, 0.545, 0.405);
  tail.rotation.x = -0.38;
  g.add(tail);

  // The cone's turn is baked into the geometry so the scale below flattens it
  // across the axis (a broad tail fan) instead of shortening it into a stub.
  const tailBase = mesh(
    geo('goose-tail-base', () => {
      const c = new THREE.ConeGeometry(0.125, 0.22, 8);
      c.rotateX(Math.PI / 2);
      return c;
    }),
    plume,
  );
  tailBase.scale.set(1.25, 0.52, 1);
  tail.add(tailBase);

  // Five feathers in a wide fan. Three narrow ones vanished behind the body
  // whenever the camera sat behind the goose — which is most of the game.
  for (let i = -2; i <= 2; i++) {
    const f = mesh(featherGeo(0.185, 0.078), featherMat, { receive: false });
    f.position.set(i * 0.038, 0.010 - Math.abs(i) * 0.008, 0.03);
    f.rotation.set(-0.12, Math.PI + i * 0.26, i * 0.12);
    tail.add(f);
  }

  // ── Legs and feet ─────────────────────────────────────────────────────────
  for (const s of [-1, 1]) {
    const leg = mesh(
      geo('goose-leg', () => new THREE.CylinderGeometry(0.028, 0.033, 0.22, 8)),
      footMat,
    );
    leg.position.set(0.105 * s, 0.115, 0.015);
    g.add(leg);

    const foot = mesh(webbedFoot(0.19), footMat);
    foot.position.set(0.105 * s, 0.022, -0.040);
    // Splayed outward, and not by quite the same amount on each side.
    foot.rotation.y = (s < 0 ? -0.20 : 0.26);
    g.add(foot);
  }

  // Faint contact shadow so the bird is planted rather than hovering, on the
  // frames where the real shadow map is too coarse to show one.
  const contact = mesh(
    geo('goose-contact', () => new THREE.CircleGeometry(0.34, 18)),
    mat(0x000000, { flat: true, transparent: true, opacity: 0.16, depthWrite: false }),
    { cast: false, receive: false },
  );
  contact.rotation.x = -Math.PI / 2;
  contact.position.y = 0.006;
  contact.renderOrder = -1;
  g.add(contact);

  // ── Contract surface ──────────────────────────────────────────────────────
  // head/beak anchors hang off the head group so hats and bill cosmetics ride
  // the bob; body/feet/aura stay on the root so breathing does not pump them.
  g.userData.attach = {
    head: makeAnchor(head, 'attach-head', 0, 0.105, -0.012),
    beak: makeAnchor(head, 'attach-beak', 0, -0.010, -0.150),
    body: makeAnchor(g, 'attach-body', 0, 0.50, 0.02),
    feet: makeAnchor(g, 'attach-feet', 0, 0.03, -0.02),
    aura: makeAnchor(g, 'attach-aura', 0, 0.50, 0.02),
  };
  g.userData.parts = { body, head, neck, beak, tail };
  g.userData.color = bodyColor;
  // Unused by the runtime, but it keeps the magic numbers discoverable for
  // anyone writing a new animation hook.
  g.userData.rest = { bodyScaleY: BREATH_REST_Y, neckRotX: NECK_REST_X };
  return g;
}

/**
 * A stylised goose wing used as a first-person hand (GDD 3.3).
 * Origin at the wrist; the wing extends along −Z.
 *
 * @param {'left'|'right'} side
 * @returns {THREE.Group} with `userData.setPose('open'|'grip'|'point')`.
 */
export function createWingHand(side = 'right') {
  const g = new THREE.Group();
  g.name = `wing-hand-${side}`;
  const s = side === 'left' ? -1 : 1;

  const base = ART.cream;
  const tex = featherFace(shade(base, 0.08), 0xffffff);
  const featherMat = mat(0xffffff, { rough: 0.88, map: tex, side: THREE.DoubleSide });

  // The wrist knuckle — small, because the feathers are the read.
  const palm = mesh(
    geo('wingh-palm', () => new THREE.SphereGeometry(0.052, 10, 8)),
    mat(base, { rough: 0.85 }),
  );
  palm.scale.set(1, 0.62, 1.2);
  g.add(palm);

  // Four primaries in a layered fan. Each lives in its own pivot group so a
  // pose rotates the feather about its quill rather than sliding it.
  const feathers = new THREE.Group();
  feathers.name = 'feathers';
  g.add(feathers);

  const LAYOUT = [
    { len: 0.175, w: 0.062, x: 0.052, y: 0.004, yaw: 0.30 },
    { len: 0.205, w: 0.068, x: 0.020, y: 0.010, yaw: 0.11 },
    { len: 0.200, w: 0.066, x: -0.014, y: 0.006, yaw: -0.07 },
    { len: 0.165, w: 0.058, x: -0.044, y: -0.004, yaw: -0.24 },
  ];

  LAYOUT.forEach((f, i) => {
    const pivot = new THREE.Group();
    pivot.name = `feather-${i}`;
    pivot.position.set(f.x * s, f.y, -0.028 - i * 0.006);
    pivot.rotation.y = f.yaw * s;
    pivot.userData.restX = -0.06 + i * 0.02;
    pivot.rotation.x = pivot.userData.restX;
    feathers.add(pivot);

    const blade = mesh(featherGeo(f.len, f.w), featherMat, { receive: false });
    pivot.add(blade);
  });

  // Alula — the little thumb feather off the leading edge.
  const thumb = new THREE.Group();
  thumb.name = 'thumb';
  thumb.position.set(0.048 * s, -0.004, -0.016);
  thumb.rotation.y = -0.62 * s;
  g.add(thumb);
  thumb.add(mesh(featherGeo(0.082, 0.040), featherMat, { receive: false }));

  /**
   * Poses. Open splays the fan, grip curls it into a cup around a held object,
   * point closes the fan and extends it forward.
   * @param {'open'|'grip'|'point'} pose
   */
  g.userData.setPose = (pose) => {
    const curl = pose === 'grip' ? 0.85 : pose === 'point' ? -0.18 : 0;
    const spread = pose === 'grip' ? 0.45 : pose === 'point' ? 0.25 : 1;
    feathers.children.forEach((pivot, i) => {
      pivot.rotation.x = pivot.userData.restX + curl * (0.62 + i * 0.13);
      const layout = LAYOUT[i];
      pivot.rotation.y = layout.yaw * s * spread;
      pivot.rotation.z = curl * 0.16 * (i - 1.5) * s;
    });
    thumb.rotation.z = curl * 0.7 * s;
    thumb.rotation.y = (-0.62 + curl * 0.30) * s;
  };
  g.userData.setPose('open');
  g.userData.feathers = feathers;
  return g;
}
